import {
    MembershipBillingFrequency,
    type StripeSubscriptionSummary,
    SUBSCRIPTION_PRODUCT_BILLING_FREQUENCIES,
    SubscriptionProductId,
} from '@fruitful/types';
import { useEffect } from 'react';

import { requestBuilder } from '../../http/RequestBuilder';

export const DEFAULT_PREMIUM_BILLING_FREQUENCY =
    MembershipBillingFrequency.YEARLY;

export const PREMIUM_BILLING_FREQUENCY_DISPLAY_ORDER: MembershipBillingFrequency[] =
    [MembershipBillingFrequency.YEARLY, MembershipBillingFrequency.QUARTERLY];

/** Partial shape of the backend's plan-change preview response — only the fields this screen renders. */
export interface UpgradeToPremiumPreview {
    stripePreview: {
        amountDueNowCents: number;
        currency: string | null;
    };
    warnings: string[];
}

export const usePreviewUpgradeToPremium = requestBuilder.mutation<
    UpgradeToPremiumPreview,
    { billingFrequency: MembershipBillingFrequency }
>({
    endpoint: '/api/billing/subscription/upgrade/preview',
    method: 'POST',
});

/** No dedicated pricing endpoint — reuses preview, once per frequency, so the toggle can show both costs upfront. */
export const usePremiumFrequencyPricing = (): {
    pricing: Partial<
        Record<MembershipBillingFrequency, UpgradeToPremiumPreview>
    >;
    isLoading: boolean;
    error: string | null;
} => {
    const frequencies =
        SUBSCRIPTION_PRODUCT_BILLING_FREQUENCIES[SubscriptionProductId.PREMIUM];
    const quarterly = usePreviewUpgradeToPremium();
    const yearly = usePreviewUpgradeToPremium();

    useEffect(() => {
        if (frequencies.includes(MembershipBillingFrequency.QUARTERLY)) {
            quarterly.mutate({
                body: {
                    billingFrequency: MembershipBillingFrequency.QUARTERLY,
                },
            });
        }
        if (frequencies.includes(MembershipBillingFrequency.YEARLY)) {
            yearly.mutate({
                body: { billingFrequency: MembershipBillingFrequency.YEARLY },
            });
        }
    }, []);

    const error = quarterly.error ?? yearly.error;
    return {
        pricing: {
            [MembershipBillingFrequency.QUARTERLY]: quarterly.data,
            [MembershipBillingFrequency.YEARLY]: yearly.data,
        },
        isLoading: quarterly.isPending || yearly.isPending,
        error: error instanceof Error ? error.message : null,
    };
};

export interface UpgradeToPremiumResult {
    subscription?: StripeSubscriptionSummary;
}

/** Send exactly one of `paymentMethodId` (card form) or `setupIntentId` (PaymentSheet). */
export const useUpgradeToPremium = requestBuilder.mutation<
    UpgradeToPremiumResult,
    {
        billingFrequency: MembershipBillingFrequency;
        paymentMethodId?: string;
        setupIntentId?: string;
    }
>({
    endpoint: '/api/billing/subscription/upgrade',
    method: 'POST',
});
